import { useMemo, useState, type ReactNode } from 'react'
import {
  PanelStackContext,
  type PanelStackValue,
  type PanelToggleOptions,
} from './panelStackContextObject'

type PanelId = 'chat' | 'support'
type PanelFlags = Record<PanelId, boolean>

const NO_PANELS: PanelFlags = { chat: false, support: false }

/**
 * The two global side panels (chat, support). Opening one replaces the other
 * unless opened "alongside"; a replaced panel stays mounted (hidden) so its
 * draft or conversation survives until it is closed.
 */
export function PanelStackProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<PanelFlags>(NO_PANELS)
  const [mounted, setMounted] = useState<PanelFlags>(NO_PANELS)
  const [chatSession, setChatSession] = useState<PanelStackValue['chatSession']>(null)
  const [isChatProtected, setChatProtected] = useState(false)

  const value = useMemo<PanelStackValue>(() => {
    const close = (id: PanelId) => {
      if (id === 'chat') {
        setChatSession(null)
        setChatProtected(false)
      }
      setOpen((current) => ({ ...current, [id]: false }))
      setMounted((current) => ({ ...current, [id]: false }))
    }

    const toggle = (id: PanelId, options?: PanelToggleOptions) => {
      if (open[id]) {
        close(id)
        return
      }
      setOpen((current) => ({ ...(options?.alongside ? current : NO_PANELS), [id]: true }))
      setMounted((current) => ({ ...current, [id]: true }))
    }

    return {
      chatSession,
      isChatProtected,
      setChatProtected,
      dockChat: (chat) => {
        if (isChatProtected) return false
        setChatSession((previous) => ({ ...chat, key: (previous?.key ?? 0) + 1 }))
        setOpen({ chat: true, support: false })
        setMounted((current) => ({ ...current, chat: true }))
        return true
      },
      isChatOpen: open.chat,
      isChatMounted: mounted.chat,
      toggleChat: (options) => toggle('chat', options),
      closeChat: () => close('chat'),
      isSupportOpen: open.support,
      isSupportMounted: mounted.support,
      toggleSupport: (options) => toggle('support', options),
      closeSupport: () => close('support'),
    }
  }, [open, mounted, chatSession, isChatProtected])

  return <PanelStackContext.Provider value={value}>{children}</PanelStackContext.Provider>
}
